// authService.js
import {
  makeAuthenticatedRequest,
  makeAuthenticatedRequestWithHandling,
} from "./api";
import { clearToken } from "./tokenStorage";

const userRegister = async (credentials) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      "/auth/register",
      "POST",
      JSON.stringify(credentials),
    );

    if (!response.ok) {
      return { ok: false, error: response };
    }

    return { ok: true, data: await response.json() };
  } catch (error) {
    console.error("Login error:", error);
    return { ok: false, error: error.message };
  }
};

const validateToken = async (token) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      "/auth/validate_token",
      "POST",
      JSON.stringify({ token }),
    );

    if (!response.ok) {
      const errorData = response;
      return { ok: false, error: errorData.error };
    }

    return { ok: true, data: await response.json() };
  } catch (error) {
    console.error("Token validation error:", error);
    return { ok: false, error: error.message };
  }
};

const verifyUserOtp = async (email) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      "/auth/verify_user",
      "POST",
      email,
    );

    if (!response.ok) {
      return { ok: false, error: response };
    }

    return { ok: true, data: await response.json() };
  } catch (error) {
    console.error("Login error:", error);
    return { ok: false, error: error.message };
  }
};
const checkUserOtp = async (data) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      "/auth/check_otp",
      "POST",
      data,
    );

    if (!response.ok) {
      return { ok: false, error: response };
    }

    return { ok: true, data: await response.json() };
  } catch (error) {
    console.error("Login error:", error);
    return { ok: false, error: error.message };
  }
};
const resendOtp = async (email) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      "/auth/send_otp",
      "POST",
      JSON.stringify(email),
    );

    if (!response.ok) {
      return { ok: false, error: response };
    }

    return { ok: true, data: await response.json() };
  } catch (error) {
    console.error("Login error:", error);
    return { ok: false, error: error.message };
  }
};

const userLogin = async (credentials) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      "/auth/login",
      "POST",
      JSON.stringify(credentials),
    );

    if (!response.ok) {
      const errorData = await response;
      return { ok: false, error: errorData.error };
    }

    return { ok: true, data: await response.json() };
  } catch (error) {
    console.error("Login error:", error);
    return { ok: false, error: error.message };
  }
};

const changePassword = async (credentials) => {
  //   Sample request
  // {"phone":"+923234096656", "oldPassword":"test1234", "newPassword":"test123456"}
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      "/users/change_password",
      "POST",
      JSON.stringify(credentials),
    );

    if (!response.ok) {
      return { ok: false, error: response };
    }

    return { ok: true, data: await response.json() };
  } catch (error) {
    console.error("Login error:", error);
    return { ok: false, error: error.message };
  }
};

const sendOtp = async (email) => {
  try {
    return await makeAuthenticatedRequest("/auth/send_otp", "POST", email)
      .then((response) => {
        // Check if the request was successful (status code 200-299)
        if (!response.ok) {
          return { ok: false, error: response.error };
        }
        // Parse the response as JSON
        return response.json();
      })
      .then((data) => {
        // Handle the JSON data
        return { ok: true, data: data };
      })
      .catch((error) => {
        // Handle errors
        console.error("Fetch error:", error);
        return { ok: false, error: error };
      });
  } catch (error) {
    console.error("Error sending OTP:", error);
    return { ok: false, error: "Unexpected error during OTP generation." };
  }
};

const verifyOtp = async (phoneNumber, otp) => {
  try {
    const response = await makeAuthenticatedRequest("/verify-otp", "POST", {
      phoneNumber,
      otp,
    });

    if (response.ok) {
      return { ok: true };
    } else {
      return { ok: false, error: response.error };
    }
  } catch (error) {
    console.error("Error verifying OTP:", error);
    return { ok: false, error: "Unexpected error during OTP verification." };
  }
};

const validateUser = async (phoneNumber) => {
  try {
    const response = await makeAuthenticatedRequest(
      "/auth/validate_user_phone",
      "POST",
      phoneNumber,
    );

    if (response.ok) {
      return { ok: true };
    } else {
      return { ok: false, error: response.error };
    }
  } catch (error) {
    console.error("Error verifying OTP:", error);
    return { ok: false, error: "Unexpected error during OTP verification." };
  }
};

const resetPassword = async (data) => {
  try {
    return await makeAuthenticatedRequest("/auth/reset_password", "POST", data)
      .then((response) => {
        // Check if the request was successful (status code 200-299)
        if (!response.ok) {
          return { ok: false, error: response.error };
        }
        // Parse the response as JSON
        return response.json();
      })
      .then((data) => {
        // Handle the JSON data
        return { ok: true, data: data };
      })
      .catch((error) => {
        // Handle errors
        console.error("Fetch error:", error);
        return { ok: false, error: error };
      });
  } catch (error) {
    console.error("Error resetting password:", error);
    return { ok: false, error: "Unexpected error during password reset." };
  }
};

const checkUserExists = async (phoneNumber) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/users?phone=%2B${phoneNumber}`,
      "GET",
    );

    if (response.ok) {
      return response; // Assuming the server sends { ok: true, exists: true } or { ok: true, exists: false }
    } else {
      return false; // Return false if there's an error or user doesn't exist
    }
  } catch (error) {
    console.error("Error checking user existence:", error);
    return false;
  }
};

const logout = async () => {
  try {
    // 1. Clear local authentication data
    clearToken(); // Adjust storage mechanism as needed

    // 2. Redirect to the login page or a neutral page
    window.location.href = "/login"; // Adjust URL as needed
  } catch (error) {
    console.error("Logout error:", error);
    // Optionally handle logout errors here, e.g., display an error message
  }
};

export {
  validateToken,
  userRegister,
  verifyUserOtp,
  checkUserOtp,
  resendOtp,
  userLogin,
  changePassword,
  sendOtp,
  verifyOtp,
  validateUser,
  resetPassword,
  checkUserExists,
  logout,
};
